/**
 * Resolve the backlink tabs a detail page should render for one subject.
 *
 * Joins the backend's per-provider counts to the frontend component map, drops
 * anything the viewer cannot reach, and returns entries the host can render a
 * tab strip from — label, count and gating all resolved BEFORE any panel is
 * downloaded (`docs/platform/entity-relationship-surfaces-design.md` §4.2).
 *
 * Two independent gates, deliberately both:
 *
 * - **Module accessibility**, read synchronously off the loaded auth user via
 *   `ModuleContext` — the same `isModuleAccessible` the dashboard uses. A tenant
 *   without the support module renders no support tab and fires no request for
 *   one. The backend drops the same providers from the counts response, so this
 *   is not the security boundary; it is what stops the tab from flickering into
 *   existence while the counts are in flight.
 * - **A registered component**, because a backend key with no frontend entry
 *   would otherwise render a tab that opens to nothing. It is skipped and logged
 *   instead — and `tools/check_backlink_provider_parity.py` fails the build so
 *   the log line is never the first anyone hears of it.
 */

import { useCallback, useMemo } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  getGetRelatedCountsApiV1EntityLinksSubjectTypeSubjectIdRelatedCountsGetQueryKey,
  useGetRelatedCountsApiV1EntityLinksSubjectTypeSubjectIdRelatedCountsGet,
} from '../../../generated/api/shared-entity-links/shared-entity-links';
import { useModule } from '../../contexts/ModuleContext';
import { BACKLINK_PANELS, type BacklinkPanelEntry } from './backlinkPanelComponents';

/** One renderable backlink tab: the map entry plus this subject's count. */
export interface BacklinkPanelTab extends BacklinkPanelEntry {
  count: number;
}

/** The query key of a subject's counts, for invalidation after a linked write. */
export const backlinkCountsQueryKey = (subjectType: string, subjectId: string) =>
  getGetRelatedCountsApiV1EntityLinksSubjectTypeSubjectIdRelatedCountsGetQueryKey(
    subjectType,
    subjectId
  );

/**
 * Refresh EVERY backlink query for one subject — the counts and any panel list
 * already in cache.
 *
 * A write that creates or moves a link changes both, and invalidating only the
 * list leaves a stale badge next to a fresh panel (the two would then disagree,
 * which is the one thing §5 asks of a count). Matched by URL prefix rather than
 * by enumerating provider keys, so a module contributing a new panel tomorrow is
 * covered without touching this.
 */
export function invalidateBacklinks(
  queryClient: QueryClient,
  subjectType: string,
  subjectId: string
): Promise<void> {
  const prefix = `/api/v1/entity-links/${subjectType}/${subjectId}/`;
  return queryClient.invalidateQueries({
    predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(prefix),
  });
}

export function useBacklinkPanels(subjectType: string, subjectId: string) {
  const { accessibleModules } = useModule();

  const { data, isLoading } = useGetRelatedCountsApiV1EntityLinksSubjectTypeSubjectIdRelatedCountsGet(
    subjectType,
    subjectId,
    { query: { enabled: Boolean(subjectId) } }
  );

  const accessibleIds = useMemo(
    () => new Set(accessibleModules.map((m) => m.id)),
    [accessibleModules]
  );

  const isRenderable = useCallback(
    (moduleId: string, key: string) => {
      if (!accessibleIds.has(moduleId)) return false;
      if (!(key in BACKLINK_PANELS)) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`Backlink provider '${key}' has no registered panel — skipping`);
        }
        return false;
      }
      return true;
    },
    [accessibleIds]
  );

  const tabs: BacklinkPanelTab[] = useMemo(
    () =>
      (data?.data ?? [])
        .filter((c) => isRenderable(c.module_id, c.key))
        .map((c) => ({ ...BACKLINK_PANELS[c.key], count: c.count })),
    [data, isRenderable]
  );

  return { tabs, isLoading };
}
